/**
 * Transaction Info Card Component
 * TASK-213: Build Transaction Detail Modal
 *
 * Displays core transaction details: amount, client, beneficiary, source, purpose
 */

import React from 'react';
import { User, ArrowRight, Building2, Globe } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { TransactionAlert } from '../../types/compliance';
import { formatCurrency, formatDateTime } from '../../utils/formatters';

interface TransactionInfoCardProps {
  /** Transaction alert data */
  transactionAlert: TransactionAlert;
}

const TransactionInfoCard: React.FC<TransactionInfoCardProps> = ({
  transactionAlert,
}) => {
  const {
    relatedClientName,
    transactionType,
    amount,
    currency,
    timestamp,
    beneficiary,
    source,
    purpose,
  } = transactionAlert;

  // Format transaction type for display
  const formatTransactionType = (type: string): string => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Transaction Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client */}
          <div>
            <label className="text-sm font-medium text-gray-500 flex items-center gap-1 mb-1">
              <User className="w-4 h-4" />
              Client
            </label>
            <p className="text-base text-gray-900 font-medium">
              {relatedClientName || 'Unknown'}
            </p>
          </div>

          {/* Transaction Type */}
          <div>
            <label className="text-sm font-medium text-gray-500 mb-1 block">
              Type
            </label>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {formatTransactionType(transactionType)}
            </span>
          </div>

          {/* Amount */}
          <div>
            <label className="text-sm font-medium text-gray-500 mb-1 block">
              Amount
            </label>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(amount, currency)}
            </p>
          </div>

          {/* Date & Time */}
          <div>
            <label className="text-sm font-medium text-gray-500 mb-1 block">
              Date & Time
            </label>
            <p className="text-base text-gray-900">
              {formatDateTime(timestamp)}
            </p>
          </div>
        </div>

        {/* Beneficiary (for outgoing transactions) */}
        {beneficiary && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="text-sm font-medium text-gray-500 flex items-center gap-1 mb-2">
              <ArrowRight className="w-4 h-4" />
              Beneficiary
            </label>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-gray-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-base font-medium text-gray-900">
                    {beneficiary.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {beneficiary.bank}
                    {beneficiary.swift && ` • ${beneficiary.swift}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Globe className="w-4 h-4" />
                <span>
                  {beneficiary.city && `${beneficiary.city}, `}
                  {beneficiary.country}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Source (for incoming transactions) */}
        {source && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="text-sm font-medium text-gray-500 flex items-center gap-1 mb-2">
              <ArrowRight className="w-4 h-4 transform rotate-180" />
              Source
            </label>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Type:</span> {source.type}
              </p>
              {source.originCountry && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Origin:</span>{' '}
                  {source.originBank && `${source.originBank}, `}
                  {source.originCountry}
                </p>
              )}
              {source.accountType && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Account Type:</span> {source.accountType}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Purpose */}
        {purpose && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="text-sm font-medium text-gray-500 mb-1 block">
              Purpose
            </label>
            <p className="text-base text-gray-900">{purpose}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionInfoCard;
